import { MedicalClaimTypes } from "../medicalClaim";
import { RuleCondition } from "../medicalRules";

export enum QUEUE_RULE_CLAIM_SELECTION_TYPES {
  // medical_claims && medical_claim_versions
  assignment_of_benefits = "Assignment of Benefits",
  bill_type_code = "Bill Type Code",
  billed_amount = "Billed Amount",
  billing_provider_first_name = "Billing Provider First Name",
  billing_provider_last_name_or_organization_name = "Billing Provider Last Name or Organization Name",
  billing_provider_primary_taxonomy_code = "Billing Provider Taxonomy",
  billing_provider_state = "Billing Provider State",
  billing_provider_tin = "Billing Provider TIN",
  claim_status = "Claim Status",
  claim_type = "Claim Type",
  custom_pend_reason = "Custom Pend Reason",
  did_auto_adjudicate = "Did Auto Adjudicate",
  direct_member_reimbursement_indicator = "Direct Member Reimbursement Indicator",
  frequency_type_code = "Frequency Type Code",
  is_nsa_claim = "Is NSA Claim",
  last_processed_by = "Last Processed By",
  network = "Network",
  network_type = "Network Type",
  network_source = "Submitter",
  pend_reason = "Standard Pend Reason",
  place_of_service = "Place of Service",
  received_at = "Claim Received Date",
  rendering_provider_first_name = "Rendering Provider First Name",
  rendering_provider_last_name_or_organization_name = "Rendering Provider Last Name or Organization Name",
  rendering_provider_primary_taxonomy_code = "Rendering Provider Taxonomy",
  total_plan_pay_amount = "Plan Pay Amount",
}

export enum QUEUE_RULE_MEMBER_SELECTION_TYPES {
  external_group_id = "Member Group",
  external_member_id = "External Member ID",
  // member versions
  member_age = "Member Age",
  member_first_name = "Member First Name",
  member_last_name = "Member Last Name",
  member_social_security_number = "Member Social Security Number",
  member_state = "Member State",
  member_zip_code = "Member Zip Code",
  person_code = "Person Code",
  subscriber_last_name = "Subscriber Last Name",
}

export enum QUEUE_RULE_SERVICE_LINE_SELECTION_TYPES {
  // medical_service_line_item_versions
  additional_diagnosis_code = "Additional Diagnosis",
  admitting_diagnosis_code = "Admitting Diagnosis",
  amount_applied_to_deductible = "Service Line Deductible Amount",
  any_procedure_code = "Procedure Code",
  any_preventive_category = "Any Preventive Category",
  any_service_category = "Any Service Category",
  claim_adjustment_group_code = "Claim Adjustment Group Code",
  claim_adjustment_reason_code = "Claim Adjustment Reason Code",
  principal_diagnosis_code = "Principal Diagnosis",
  procedure_modifier = "Modifier Codes",
  remark_code = "Remark Code",
  revenue_code = "Revenue Code",
  service_date = "Date of Service",
}

export const QUEUE_RULE_SELECTION_TYPES = {
  ...QUEUE_RULE_CLAIM_SELECTION_TYPES,
  ...QUEUE_RULE_MEMBER_SELECTION_TYPES,
  ...QUEUE_RULE_SERVICE_LINE_SELECTION_TYPES,
};

export type QueueRuleSelectionType = keyof typeof QUEUE_RULE_SELECTION_TYPES;

export interface MedicalQueue {
  readonly id: number;
  readonly medical_queue_setting_id: number;

  readonly assigned_users?: ReadonlyArray<string>;
  readonly claim_configurations?: {
    readonly service_line_columns?: {
      readonly always_show_cob: boolean;
      readonly dental?: {
        readonly standard: boolean;
        readonly columns: ReadonlyArray<{ name: string; order: number }>;
      };
      readonly institutional?: {
        readonly standard: boolean;
        readonly columns: ReadonlyArray<{ name: string; order: number }>;
      };
      readonly professional?: {
        readonly standard: boolean;
        readonly columns: ReadonlyArray<{ name: string; order: number }>;
      };
      readonly vision?: {
        readonly standard: boolean;
        readonly columns: ReadonlyArray<{ name: string; order: number }>;
      };
    };
  };
  readonly claim_status?: "finalized" | "pended" | "--";
  readonly claim_type?: MedicalClaimTypes;
  readonly deleted_at?: string;
  readonly deleted_by?: string;
  readonly description?: string;
  readonly effective_date?: string;
  readonly end_date?: string;
  readonly identity?: string;
  readonly is_active?: boolean | string;
  readonly is_default?: boolean;
  readonly medical_queue_id?: number;
  readonly name?: string;
  readonly order?: number;
  readonly overdue_claim_count?: number;
  readonly rn?: string;
  readonly rules?: ReadonlyArray<ReadonlyArray<RuleCondition<QueueRuleSelectionType>>>;
  readonly start_date?: string;
  readonly termination_date?: string;
  readonly total_claim_count?: number;
  readonly total_plan_pay?: number;
  readonly claim_ids?: number[];
}

export interface MedicalQueueRule {
  readonly claim_type: "dental" | "medical" | "vision" | "--";
  readonly claim_status: "finalized" | "pended" | "--";
  readonly is_active?: boolean;
  readonly rules: ReadonlyArray<ReadonlyArray<RuleCondition<QueueRuleSelectionType>>>;
  readonly description: string;
  readonly name: string;
}

export interface MedicalQueueSetting {
  readonly client_ids: ReadonlyArray<string>;
  readonly id: number;
  readonly name: string;

  readonly end_date?: string;
  readonly identity?: string;
  readonly manual_overrides?: { [key: string]: number };
  readonly claim_configurations?: {
    readonly service_line_columns?: {
      readonly always_show_cob: boolean;
      readonly dental: {
        readonly standard: boolean;
        readonly columns: ReadonlyArray<{ name: string; order: number }>;
      };
      readonly institutional: {
        readonly standard: boolean;
        readonly columns: ReadonlyArray<{ name: string; order: number }>;
      };
      readonly professional: {
        readonly standard: boolean;
        readonly columns: ReadonlyArray<{ name: string; order: number }>;
      };
      readonly vision: {
        readonly standard: boolean;
        readonly columns: ReadonlyArray<{ name: string; order: number }>;
      };
    };
  };
  readonly medical_queue_group?: string;
  readonly medical_queue_setting_id?: number;
  readonly order?: ReadonlyArray<number>;
  readonly rn?: string;
  readonly start_date?: string;
  readonly processing_error?: string;
}
